import React, { useState, useEffect } from 'react';
import { checkConnectionStatus, ConnectionStatus } from '../../services/apiService';

interface ConnectionStatusProps {
  showDetails?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const ConnectionStatusComponent: React.FC<ConnectionStatusProps> = ({
  showDetails = false,
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
}) => {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const result = await checkConnectionStatus();
      setStatus(result);
    } catch (error) {
      setStatus({
        isConnected: false,
        lastChecked: new Date().toISOString(),
        error: 'Failed to check connection',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial check
    checkStatus();

    // Set up auto-refresh if enabled
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(checkStatus, refreshInterval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefresh, refreshInterval]);

  const getStatusIcon = () => {
    if (loading) return '🔄';
    if (!status) return '❓';
    return status.isConnected ? '🟢' : '🔴';
  };

  const getStatusText = () => {
    if (loading) return 'Checking...';
    if (!status) return 'Unknown';
    return status.isConnected ? 'Connected' : 'Disconnected';
  };

  const getStatusClass = () => {
    if (loading) return 'checking';
    if (!status) return 'unknown';
    return status.isConnected ? 'connected' : 'disconnected';
  };

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString();
  };

  return (
    <div className={`connection-status ${getStatusClass()}`}>
      <div className="status-indicator" onClick={checkStatus} title="Click to refresh">
        <span className="status-icon">{getStatusIcon()}</span>
        <span className="status-text">{getStatusText()}</span>
      </div>

      {showDetails && status && (
        <div className="status-details">
          <div className="detail-item">
            <span className="label">Last Checked:</span>
            <span className="value">{formatTime(status.lastChecked)}</span>
          </div>
          
          {status.responseTime && (
            <div className="detail-item">
              <span className="label">Response Time:</span>
              <span className="value">{status.responseTime}ms</span>
            </div>
          )}
          
          {status.error && (
            <div className="detail-item error">
              <span className="label">Error:</span>
              <span className="value">{status.error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionStatusComponent;